import React, { useMemo } from "react";
import { useTranslate } from "@refinedev/core";
import {
  FunnelCard,
  type FunnelStep,
  type FunnelConnector,
} from "./FunnelCard";
import { formatPct } from "./utils/";
import { analyticsTheme } from "./analyticsTheme";
import { DocumentSummaryStats } from "types/";

const T_PREFIX = "media.projects.analytics.documentHighlighter.readingFunnel";

export interface ReadingFunnelCardProps {
  data: DocumentSummaryStats | undefined;
  loading?: boolean;
}

export const ReadingFunnelCard: React.FC<ReadingFunnelCardProps> = ({
  data,
  loading,
}) => {
  const t = useTranslate();
  const impressionCount = data?.impression_sessions ?? 0;
  const scrollRate = data?.scroll_rate_pct ?? 0;
  const completionRate = data?.completion_rate_pct ?? 0;
  const deepReadPct =
    impressionCount > 0
      ? ((data?.deep_read_sessions ?? 0) / impressionCount) * 100
      : 0;

  const steps = useMemo<FunnelStep[]>(
    () => [
      {
        label: t(`${T_PREFIX}.impression`),
        info: t(`${T_PREFIX}.impressionInfo`),
        percentage: impressionCount > 0 ? 100 : 0,
        count: impressionCount,
        color: analyticsTheme.datasetCharcoal,
      },
      {
        label: t(`${T_PREFIX}.scrolled`),
        info: t(`${T_PREFIX}.scrolledInfo`),
        percentage: scrollRate,
        count: data?.scroll_sessions ?? 0,
        color: analyticsTheme.datasetBlue,
      },
      {
        label: t(`${T_PREFIX}.deepRead`),
        info: t(`${T_PREFIX}.deepReadInfo`),
        percentage: deepReadPct,
        count: data?.deep_read_sessions ?? 0,
        color: analyticsTheme.datasetAmber,
      },
      {
        label: t(`${T_PREFIX}.completed`),
        info: t(`${T_PREFIX}.completedInfo`),
        percentage: completionRate,
        count: data?.completed_sessions ?? 0,
        color: analyticsTheme.datasetEmerald,
      },
    ],
    [impressionCount, scrollRate, data, deepReadPct, completionRate, t]
  );

  const connectors = useMemo<FunnelConnector[]>(
    () => [
      {
        text: `${formatPct(scrollRate)} · ${t(T_PREFIX + ".ofTotal")}`,
      },
      {
        text: `${formatPct(deepReadPct)} ${t(T_PREFIX + ".ofTotal")}`,
      },
      {
        text: `${formatPct(completionRate)} ${t(T_PREFIX + ".ofTotal")}`,
      },
    ],
    [scrollRate, deepReadPct, completionRate, t]
  );

  return (
    <FunnelCard
      title={t(`${T_PREFIX}.title`)}
      steps={steps}
      connectors={connectors}
      loading={loading}
    />
  );
};
